import { create } from 'zustand';

import {
  CustomUserDTO,
  DirectionType,
  FileValidationSettingsDTO,
  IndexDTO,
  IndexObjectType,
  ProcessDTO,
  ProcessStepDTO,
  TagDTO,
} from '@/types/api-types';
import { NormalizedState } from '@/types/file-explorer-types';

import { apiService } from '@/services/api-service';

import { START_STEP_NAME } from '@/constants/global-constants';

import { useApiManagerStore } from './apiManagerStore';
import { useFileExplorerStore } from './fileExplorerStore';

interface GlobalState {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  allTags: NormalizedState<TagDTO>;
  setAllTags: (tags: NormalizedState<TagDTO>) => void;
  // Processes
  processes: ProcessDTO[];
  fetchProcesses: () => Promise<void>;
  createProcess: (process: Omit<ProcessDTO, 'uuid'>) => Promise<ProcessDTO>;
  editProcess: (process: ProcessDTO) => Promise<ProcessDTO>;

  getProcessByUuid: (uuid: ProcessStepDTO['uuid']) => ProcessDTO | null;
  getProcessByIndexUuid: (indexUuid: IndexDTO['uuid']) => ProcessDTO | null;
  deleteProcess: (id: string) => Promise<void>;

  //TODO: think about this
  fileUuidToMove: string | null;
  setFileUuidToMove: (fileUuid: string | null) => void;
  isMoveFileDialogOpen: boolean;
  setIsMoveFileDialogOpen: (open: boolean) => void;

  // Navigation source tracking for back button
  navigationSource: 'myDocuments' | 'searchResults' | null;
  setNavigationSource: (source: 'myDocuments' | 'searchResults' | null) => void;

  fileValidationSettings: FileValidationSettingsDTO[];
  getFileValidationSettings: () => Promise<FileValidationSettingsDTO[]>;
  updateFileValidationSettings: (
    uuid: string,
    settings: Partial<FileValidationSettingsDTO>,
  ) => Promise<void>;

  findProcess: ({
    index,
    objectType,
    direction,
  }: {
    index: string;
    objectType: IndexObjectType;
    direction: DirectionType;
  }) => ProcessDTO | null;

  findFirstStep: (steps: string[]) => ProcessStepDTO;

  users: CustomUserDTO[];
  getAutoFetchUsers: (uuids?: string[]) => Promise<CustomUserDTO[]>;

  // Indexes
  indexes: IndexDTO[];
  getAutoFetchIndexes: () => Promise<IndexDTO[]>;
  getIndexes: () => Promise<void>;
  getIndexById: (uuid: string) => IndexDTO | null;
  getIndexByTypeId: (typeId: string) => IndexDTO | null;
  createIndex: (index: Omit<IndexDTO, 'uuid'>) => Promise<void>;
  updateIndex: (uuid: string, index: Partial<IndexDTO>) => Promise<void>;
  deleteIndex: (uuid: string) => Promise<void>;

  createUser: (userData: CustomUserDTO) => Promise<void>;
  updateUser: (uuid: string, userData: Partial<CustomUserDTO>) => Promise<void>;
  deleteUser: (uuid: string) => Promise<void>;
  getAllUsers: (uuids?: string[]) => Promise<CustomUserDTO[]>;

  // Steps
  steps: ProcessStepDTO[];
  getAllSteps: () => Promise<void>;
  deleteProcessStep: (stepId: string) => Promise<void>;
  bulkSaveSteps: (steps: ProcessStepDTO[]) => Promise<ProcessStepDTO['uuid'][]>;
  bulkUpdateSteps: (
    steps: ProcessStepDTO[],
  ) => Promise<ProcessStepDTO['uuid'][]>;
  bulkDeleteSteps: (uuids: ProcessStepDTO['uuid'][]) => Promise<void>;

  getGlobalSettings: () => Promise<void>;
}

export const useGlobalStore = create<GlobalState>((set, get) => ({
  fileUuidToMove: null,
  setFileUuidToMove: (fileUuid) => set({ fileUuidToMove: fileUuid }),
  isMoveFileDialogOpen: false,
  setIsMoveFileDialogOpen: (open) => {
    if (!open) {
      set({ fileUuidToMove: null });
    }
    set({ isMoveFileDialogOpen: open });
  },

  // Navigation source tracking for back button
  navigationSource: null,
  setNavigationSource: (source) => set({ navigationSource: source }),
  stateHandlers: {},
  isLoading: false,
  setIsLoading: (isLoading) => set({ isLoading }),
  allTags: {
    entities: {},
    rootIds: [],
    totalCount: 0,
  },
  setAllTags: (tags: NormalizedState<TagDTO>) => set({ allTags: tags }),

  getGlobalSettings: async () => {
    const response = await apiService.getGlobalSettings();
    set({
      indexes: response.INDEX,
      processes: response.PROCESS,
      fileValidationSettings: response.FILE_VALIDATION_SETTINGS,
      // users: response.USER,
      allTags: {
        entities: response.TAG.reduce(
          (acc, tag) => {
            acc[tag.uuid] = tag;
            return acc;
          },
          {} as Record<string, TagDTO>,
        ),
        rootIds: response.TAG.map((tag) => tag.uuid),
        totalCount: response.TAG.length,
      },
      steps: response.PROCESS_STEP,
    });

    const apiManagerState = useApiManagerStore.getState();
    const fileExplorerState = useFileExplorerStore.getState();

    apiManagerState.setDirectories(response.DIRECTORY);
    fileExplorerState.setDirectoriesState(response.DIRECTORY);

    fileExplorerState.setEntitiesCount({
      DIRECTORY: response.DIRECTORY.length,
    });
    apiManagerState.setTotalActiveAgreements(response.AGREEMENT.AGREEMENT);
  },

  processes: [],
  fetchProcesses: async () => {
    const response = await apiService.getProcesses();
    set({ processes: response });
  },
  getProcessByUuid: (uuid: ProcessStepDTO['uuid']) => {
    const process = get().processes;
    return process.find((p) => p.uuid === uuid) || null;
  },
  getProcessByIndexUuid: (indexUuid: IndexDTO['uuid']) => {
    return (
      get().processes.find((p) => p.index === indexUuid && !p.is_deleted) ||
      null
    );
  },
  deleteProcess: async (id: string) => {
    await apiService.deleteProcess(id);
    await get().fetchProcesses();
  },
  createProcess: async (newProcess: Omit<ProcessDTO, 'uuid'>) => {
    const newProcessResponse = await apiService.createProcess(newProcess);
    set((state) => ({
      processes: [...state.processes, newProcessResponse],
    }));
    return newProcessResponse;
  },
  steps: [],
  getAllSteps: async () => {
    const response = await apiService.getProcessSteps();
    set({ steps: response });
  },
  deleteProcessStep: async (stepId: string) => {
    await apiService.deleteProcessStep(stepId);
    set((state) => ({
      steps: state.steps.filter((step) => step.uuid !== stepId),
    }));
  },
  addTempProcessWithNewId: (newProcess: ProcessDTO) => {
    set((state) => ({
      processes: [...state.processes, newProcess],
    }));
  },
  deleteTempProcess: (id: string) => {
    set((state) => ({
      processes: state.processes.filter((process) => process.uuid !== id),
    }));
  },
  editProcess: async (processItem: ProcessDTO) => {
    const newProcessResponse = await apiService.updateProcess(
      processItem.uuid,
      processItem,
    );
    set((state) => ({
      processes: state.processes.map((process) =>
        process.uuid === processItem.uuid ? newProcessResponse : process,
      ),
    }));
    return newProcessResponse;
  },
  bulkSaveSteps: async (steps: ProcessStepDTO[]) => {
    const uuids = await apiService.bulkCreateProcessSteps(steps);
    set((state) => ({
      steps: [...state.steps, ...uuids],
    }));
    return uuids.map((uuid) => uuid.uuid);
  },
  bulkUpdateSteps: async (steps: ProcessStepDTO[]) => {
    const updatedSteps = await apiService.bulkUpdateProcessSteps(steps);
    set((state) => ({
      steps: state.steps.map((step) =>
        updatedSteps.some((uuid) => uuid.uuid === step.uuid)
          ? updatedSteps.find((s) => s.uuid === step.uuid)!
          : step,
      ),
    }));
    return updatedSteps.map((uuid) => uuid.uuid);
  },
  bulkDeleteSteps: async (uuids: string[]) => {
    await apiService.bulkDeleteProcessSteps(uuids);
    set((state) => ({
      steps: state.steps.filter((step) => !uuids.includes(step.uuid)),
    }));
  },
  // File Validation Settings
  fileValidationSettings: [],
  getFileValidationSettings: async () => {
    const data = await apiService.getFileValidationSettings();
    set({ fileValidationSettings: data });
    return data;
  },

  updateFileValidationSettings: async (
    uuid: string,
    settings: Partial<FileValidationSettingsDTO>,
  ) => {
    const data = await apiService.updateFileValidationSettings(uuid, settings);

    set((state) => ({
      fileValidationSettings: [
        ...state.fileValidationSettings.filter(
          (setting) => setting.uuid !== uuid,
        ),
        data,
      ],
    }));
  },

  findProcess: ({
    index,
    objectType,
    direction,
  }: {
    index: string;
    objectType: IndexObjectType;
    direction: DirectionType;
  }) => {
    const processes = get().processes.filter((process) => !process.is_deleted);
    // First try to find by index
    const byIndex = processes.find((process) => process.index === index);
    if (byIndex) return byIndex;

    // Then try to find by object type and direction
    return (
      processes.find(
        (process) =>
          process.object_type === objectType &&
          process.direction === direction &&
          process.index === null,
      ) || null
    );
  },

  findFirstStep: (steps: string[]) => {
    const stepsFromStore = get().steps.filter((step) =>
      steps.includes(step.uuid),
    );

    const firstStep = stepsFromStore.find(
      (step) => step.status_name === START_STEP_NAME,
    );
    if (!firstStep) {
      throw new Error('First step not found');
    }

    return firstStep;
  },

  users: [],
  getAllUsers: async (uuids?: string[], extra?: Record<string, unknown>) => {
    const response = await apiService.getUsers(
      uuids,
      !uuids || uuids.length === 0 ? { limit: 100 } : extra,
    );
    set({ users: response });
    return response;
  },

  createUser: async (userData) => {
    const newUser = await apiService.createUser(userData);
    set((state) => ({
      users: [...state.users, newUser],
    }));
  },

  updateUser: async (uuid, userData) => {
    const oldUser = get().users.find((user) => user.uuid === uuid);
    if (!oldUser) {
      throw new Error('User not found');
    }
    const updatedUser = await apiService.updateUser(uuid, {
      ...oldUser,
      ...userData,
    });
    set((state) => ({
      users: state.users.map((user) =>
        user.uuid === uuid ? updatedUser : user,
      ),
    }));
  },

  deleteUser: async (uuid) => {
    await apiService.deleteUser(uuid);
    set((state) => ({
      users: state.users.filter((user) => user.uuid !== uuid),
    }));
  },

  indexes: [],
  getAutoFetchIndexes: async () => {
    if (get().indexes.length === 0) {
      await get().getIndexes();
    }
    return get().indexes;
  },

  // Indexes
  getIndexes: async () => {
    const data = await apiService.getIndexes();
    set({ indexes: data });
  },
  getIndexById: (uuid: string) => {
    return get().indexes.find((index) => index.uuid === uuid) || null;
  },
  getIndexByTypeId: (typeId: string) => {
    return get().indexes.find((index) => index.name === typeId) || null;
  },
  createIndex: async (index) => {
    const data = await apiService.createIndex(index);
    set((state) => ({
      indexes: [...state.indexes, data],
    }));
  },
  updateIndex: async (uuid, index) => {
    const data = await apiService.updateIndex(uuid, index);
    set((state) => ({
      indexes: state.indexes.map((index) =>
        index.uuid === uuid ? { ...index, ...data } : index,
      ),
    }));
  },
  deleteIndex: async (uuid) => {
    await apiService.deleteIndex(uuid);
    set((state) => ({
      indexes: state.indexes.filter((index) => index.uuid !== uuid),
    }));
  },

  getAutoFetchUsers: async (uuids?: string[]) => {
    if (uuids) {
      const oldUsers = get().users;

      // Find which UUIDs we already have
      const existingUsers = oldUsers.filter((user) =>
        uuids.includes(user.uuid),
      );
      const existingUuids = existingUsers.map((user) => user.uuid);

      // Find which UUIDs we need to fetch
      const missingUuids = uuids.filter(
        (uuid) => !existingUuids.includes(uuid),
      );

      if (missingUuids.length > 0) {
        // Fetch only the missing users
        const newUsers = await get().getAllUsers(missingUuids);

        // Merge existing users with newly fetched users
        const allRequestedUsers = [...existingUsers, ...newUsers];

        // Update the store with all users (existing + new)
        const updatedUsers = [
          ...oldUsers.filter((user) => !uuids.includes(user.uuid)), // Keep users not in current request
          ...allRequestedUsers, // Add all requested users (existing + new)
        ];

        set({ users: updatedUsers });

        return allRequestedUsers;
      }

      // All requested users are already loaded
      return existingUsers;
    }
    if (get().users.length === 0) {
      await get().getAllUsers();
    }
    return get().users;
  },
}));
